import { AlertTriangle, RefreshCw } from "lucide-react";

import { useNetRequirements } from "@/api/queries";
import type { NetRequirementLine } from "@/api/types";
import { Button } from "@/components/Button";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import {
  formatNetWarning,
  formatQuantity,
  formatRelative,
  formatUnit,
  pluralize,
} from "@/lib/format";
import { shortageTone } from "@/lib/stock";

/**
 * Le besoin net d'un chantier (V1.6-02).
 *
 * Six colonnes, et aucune n'est décorative : ce qu'on veut, ce qu'on a ici, ce
 * qui est réservé, ce qu'on pourrait faire venir, ce qui est en route, et ce
 * qui manque quand même.
 *
 * La dernière colonne s'appelle **« Manque net »**, jamais « À acheter ». Aucun
 * PurchasePlan n'existe encore : ce nombre dit ce qui manque, il ne commande
 * rien et ne doit pas ressembler à un ordre.
 *
 * Le client ne recalcule aucun de ces chiffres. Ils viennent du backend avec
 * leur heure de lecture, parce qu'un manque net vieillit à la transaction
 * suivante.
 */
export function NetRequirementsPanel({ projectId }: { projectId: string }) {
  const { data, isLoading, isFetching, error, refetch } = useNetRequirements(projectId);

  if (isLoading) return <TableSkeleton rows={6} columns={7} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  if (data.lines.length === 0) {
    return (
      <EmptyState
        title="Aucun besoin confirmé"
        description="Confirmez une liste de matériel, ou ajoutez un besoin, pour voir ce qui manque vraiment."
      />
    );
  }

  const noScope = data.scope_location_id === null;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-ink-subtle">
          Calculé {formatRelative(data.calculated_at)} ·{" "}
          {pluralize(data.total_shortage_lines, "ligne")} en manque
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void refetch()}
          loading={isFetching}
        >
          <RefreshCw className="size-4" aria-hidden />
          Recalculer
        </Button>
      </div>

      {noScope && (
        <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-[12px] text-ink">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          Ce chantier n'a pas d'emplacement. Tout le stock compte comme sur place, et rien
          n'est proposé au transfert.
        </p>
      )}

      <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-surface">
        <div className="hidden md:block">
          <TableShell>
            <thead>
              <tr>
                <Th className="max-w-0">Article</Th>
                <Th align="right" className="w-24 whitespace-nowrap">
                  Besoin
                </Th>
                <Th align="right" className="w-28 whitespace-nowrap">
                  Chez nous
                </Th>
                <Th align="right" className="hidden w-24 whitespace-nowrap @3xl:table-cell">
                  Réservé
                </Th>
                <Th align="right" className="w-28 whitespace-nowrap">
                  Transfert
                </Th>
                <Th align="right" className="w-24 whitespace-nowrap">
                  Entrant
                </Th>
                <Th align="right" className="w-28 whitespace-nowrap">
                  Manque net
                </Th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((line) => (
                <NetRow key={line.requirement_id} line={line} />
              ))}
            </tbody>
          </TableShell>
        </div>

        <ul className="divide-y divide-border/60 md:hidden">
          {data.lines.map((line) => (
            <li key={line.requirement_id}>
              <NetCard line={line} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Warnings({ line }: { line: NetRequirementLine }) {
  if (line.warnings.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-col gap-0.5">
      {line.warnings.map((warning) => (
        <li key={warning} className="text-[11px] text-warning">
          {formatNetWarning(warning)}
        </li>
      ))}
    </ul>
  );
}

/** L'entrant, avec ce qui compte et ce qui ne compte pas, séparés. */
function Incoming({ line }: { line: NetRequirementLine }) {
  const late = Number(line.incoming_late) > 0;
  const unknown = Number(line.incoming_unknown_eta) > 0;
  return (
    <>
      <span className="tnum">{formatQuantity(line.incoming_on_time)}</span>
      {(late || unknown) && (
        <span className="block text-[11px] text-warning">
          {late && `+${formatQuantity(line.incoming_late)} en retard`}
          {late && unknown && " · "}
          {unknown && `+${formatQuantity(line.incoming_unknown_eta)} sans date`}
        </span>
      )}
    </>
  );
}

function NetRow({ line }: { line: NetRequirementLine }) {
  const unit = formatUnit(line.unit as never);
  return (
    <Tr>
      <Td className="max-w-0">
        <span className="block truncate font-medium text-ink">{line.description}</span>
        {line.product_id === null && (
          <span className="block text-[11px] text-ink-subtle">Hors catalogue</span>
        )}
        <Warnings line={line} />
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px]">
        {formatQuantity(line.required)} {unit}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px]">
        {formatQuantity(line.local_available)}
      </Td>
      <Td
        align="right"
        className="tnum hidden whitespace-nowrap text-[13px] text-ink-muted @3xl:table-cell"
      >
        {formatQuantity(line.reserved_total)}
        {Number(line.reserved_for_this_project) > 0 && (
          <span className="block text-[11px] text-ink-subtle">
            dont {formatQuantity(line.reserved_for_this_project)} à nous
          </span>
        )}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {formatQuantity(line.transferable)}
      </Td>
      <Td align="right" className="whitespace-nowrap text-[13px] text-ink-muted">
        <Incoming line={line} />
      </Td>
      <Td align="right" className="whitespace-nowrap">
        <StatusBadge tone={shortageTone(line.net_shortage)}>
          {formatQuantity(line.net_shortage)} {unit}
        </StatusBadge>
      </Td>
    </Tr>
  );
}

/**
 * Sur mobile, une carte plutôt qu'une table de sept colonnes qu'il faudrait
 * faire défiler latéralement pour lire un seul chiffre.
 */
function NetCard({ line }: { line: NetRequirementLine }) {
  const unit = formatUnit(line.unit as never);
  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium text-ink">{line.description}</p>
          <p className="text-[12px] text-ink-muted">
            Besoin {formatQuantity(line.required)} {unit}
          </p>
        </div>
        <StatusBadge tone={shortageTone(line.net_shortage)} className="shrink-0">
          {formatQuantity(line.net_shortage)} {unit}
        </StatusBadge>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
        <div className="flex justify-between">
          <dt className="text-ink-subtle">Chez nous</dt>
          <dd className="tnum text-ink">{formatQuantity(line.local_available)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-subtle">Réservé</dt>
          <dd className="tnum text-ink-muted">{formatQuantity(line.reserved_total)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-subtle">Transfert</dt>
          <dd className="tnum text-ink-muted">{formatQuantity(line.transferable)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-subtle">Entrant</dt>
          <dd className="tnum text-ink-muted">{formatQuantity(line.incoming_on_time)}</dd>
        </div>
      </dl>
      <Warnings line={line} />
    </div>
  );
}
